import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

interface UseExpandableTextOptions {
  /** Se o texto deve iniciar expandido. Padrão: `false`. */
  defaultExpanded?: boolean;
  /**
   * Dependências que, ao mudarem, disparam uma nova medição de overflow
   * (ex.: o próprio texto exibido). Útil quando o conteúdo é assíncrono e
   * chega após a montagem.
   */
  deps?: unknown[];
}

interface UseExpandableTextReturn<T extends HTMLElement> {
  /** Ref a ser anexada ao elemento de texto (o que recebe o `line-clamp`). */
  ref: RefObject<T | null>;
  /** Se o texto está atualmente expandido. */
  expanded: boolean;
  /**
   * Se o texto transborda quando recolhido — ou seja, se há conteúdo oculto
   * que justifique exibir um gatilho de "ver mais". Medido apenas enquanto
   * recolhido, pois expandido `scrollHeight === clientHeight`.
   */
  isOverflowing: boolean;
  /** Alterna entre expandido e recolhido. */
  toggle: () => void;
  /** Expande o texto. */
  expand: () => void;
  /** Recolhe o texto. */
  collapse: () => void;
}

/**
 * Hook reutilizável para texto expansível com detecção de overflow.
 *
 * Anexe `ref` ao elemento de texto que aplica um recorte vertical
 * (ex.: `line-clamp-*`). O hook mede se o conteúdo transborda o espaço
 * recolhido e expõe `isOverflowing` para que o consumidor decida quando
 * mostrar um gatilho de "ver mais"/"ver menos". A medição é refeita quando o
 * elemento é redimensionado (`ResizeObserver`) e quando `deps` muda.
 */
export function useExpandableText<T extends HTMLElement = HTMLElement>(
  options: UseExpandableTextOptions = {}
): UseExpandableTextReturn<T> {
  const { defaultExpanded = false, deps = [] } = options;

  const ref = useRef<T>(null);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // O clamp só pode ser detectado com segurança enquanto recolhido; ao
    // expandir, `scrollHeight` se iguala a `clientHeight` e perderíamos o
    // estado. Mantemos o último valor medido nesse caso.
    if (expanded) return;
    // Tolerância de 1px para arredondamentos sub-pixel entre navegadores.
    setIsOverflowing(el.scrollHeight - el.clientHeight > 1);
  }, [expanded]);

  // Medição síncrona antes da pintura, evitando flicker do gatilho.
  useLayoutEffect(() => {
    measure();
    // `deps` permite remedir quando o conteúdo textual muda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measure, ...deps]);

  // Remede em mudanças de layout (resize da tela, fontes, container).
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => measure());
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure]);

  const toggle = useCallback(() => setExpanded(prev => !prev), []);
  const expand = useCallback(() => setExpanded(true), []);
  const collapse = useCallback(() => setExpanded(false), []);

  return { ref, expanded, isOverflowing, toggle, expand, collapse };
}
